import React, { useState, useEffect } from 'react';
import api from '../api/axiosConfig';
import './SubjectManager.css';
import Modal from './Modal';
import EditSubjectForm from './EditSubjectForm';

const SubjectManager = () => {
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [newSubject, setNewSubject] = useState({ name: '', gradeId: localStorage.getItem('sm-lastGrade') || localStorage.getItem('dash-lastGrade') || '' });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [gradeRes, subjectRes] = await Promise.all([
        api.get('/admin/grades'),// Ensure this GET route exists in backend
      ]);
      setGrades(gradeRes.data);
      setSubjects(subjectRes.data);
    } catch (err) { console.error("Error loading data"); }
  };

  useEffect(() => {
    if (newSubject.gradeId) {
      fetchSubjects();
    }
  }, [newSubject.gradeId]);

  const fetchSubjects = async () => {
    try {
      const res = await api.get(`/admin/subjects?gradeId=${newSubject.gradeId}`);
      setSubjects(res.data);
    } catch (err) {
      console.error("Error loading subjects");
    }
  };

  const handleSaveSubject = async (updatedData) => {
    setIsSaving(true);
    try {
      await api.put(`/admin/subjects/${selectedSubject.id}`, updatedData);
      setIsEditModalOpen(false);
      fetchSubjects();
    } catch (err) {
      console.error("Failed to save subject:", err);
      alert("Failed to save subject. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/subjects', newSubject);
      setNewSubject(prev => ({ ...prev, name: '' }));
      fetchSubjects();
    } catch (err) { alert("Failed to add subject"); }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure? All topics inside this subject will also be deleted!")) {
      try {
        await api.delete(`/admin/subjects/${id}`);

        // Update the local list so the UI refreshes instantly
        setSubjects(subjects.filter(subject => subject.id !== id));
      } catch (err) {
        console.error("Delete failed:", err);
        alert("Failed to delete subject. Please try again.");
      }
    }
  };

  const handleEdit = (id) => {
    // Implement edit functionality here
    setSelectedSubject(subjects.find(s => s.id === id));
    setIsEditModalOpen(true);
  };

  const onGradeSelected = (e) => {
    const gradeId = e.target.value;
    setNewSubject(prev => ({ ...prev, gradeId: gradeId }));
    localStorage.setItem('sm-lastGrade', gradeId);
  };

  return (
    <div>
      <h2 style={{ marginBottom: '1.5rem' }}>Manage Subjects</h2>

      {/* Form to Add Subject */}
      <form onSubmit={handleSubmit} style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', marginBottom: '2rem', display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Subject Name</label>
          <input
            type="text"
            placeholder="e.g. Mathematics"
            value={newSubject.name}
            onChange={e => setNewSubject({ ...newSubject, name: e.target.value })}
            required
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Assign to Grade</label>
          <select
            value={newSubject.gradeId}
            onChange={onGradeSelected}
            required
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minWidth: '150px' }}
          >
            <option value="">Select Grade</option>
            {grades.map(g => (
              <option key={g.id} value={g.id}>{g.name} (Lvl {g.level})</option>
            ))}
          </select>
        </div>

        <button type="submit" style={{ padding: '8px 16px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', height: '38px' }}>
          Create Subject
        </button>
      </form>

      {/* Subject Table */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead style={{ backgroundColor: '#f1f5f9' }}>
            <tr>
              <th style={{ padding: '12px' }}>Subject Name</th>
              <th style={{ padding: '12px' }}>Grade</th>
              <th style={{ padding: '12px' }}>Type</th>
              <th style={{ padding: '12px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subjects.map(s => (
              <tr key={s.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={{ padding: '12px' }}>{s.name}</td>
                <td style={{ padding: '12px' }}>
                  {/* If your backend includes the Grade object, you can show s.Grade.name */}
                  <span style={{ background: '#e2e8f0', padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem' }}>
                    {s.Grade.name} (ID:  {s.gradeId})
                  </span>
                </td>
                <td style={{ padding: '12px' }}>
                  <span style={{ background: '#dbeafe', padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem' }}>
                    {s.subjectType}
                  </span>
                </td>
                <td style={{ padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => handleDelete(s.id)}>Delete</button>
                    <button style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => handleEdit(s.id)}>Edit</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Subject"
      >
        <EditSubjectForm
          subject={selectedSubject}
          onSave={handleSaveSubject}
          onCancel={() => setIsEditModalOpen(false)}
          isSubmitting={isSaving}
        />
      </Modal>
    </div>
  );
};

export default SubjectManager;